import { forwardRef } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Anatomia de página do sistema E "Cobalto" (docs/DESIGN.md):
//   PageCover  = capa cobalto com título, ações e os 3 ou 4 números que importam
//   PageBody   = onde as folhas (Card) sobem sobre a capa
//   SheetHeader, StatusDot e CoverButton completam o vocabulário.

/**
 * Capa cobalto. `figures` = [{ value, label }]; valores grandes e leves.
 * O primeiro filho de PageBody precisa ser uma folha (Card), porque sobe 48px sobre a capa.
 */
export const PageCover = ({ title, subtitle, actions, figures = [], children }) => (
  <section className="relative overflow-hidden bg-cover px-5 pb-[72px] pt-7 text-cover-foreground sm:px-10">
    {/* marca-d'água: o logotipo inteiro da BCMED, a 7%, cabendo na altura da capa */}
    <div aria-hidden className="pointer-events-none absolute inset-y-5 right-5 flex w-[min(44%,480px)] items-center justify-end opacity-[0.07] sm:right-10">
      <img src="/logo-bcmed.png" alt="" className="max-h-full w-full object-contain object-right brightness-0 invert" />
    </div>
    <div className="relative flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
      <div className="min-w-0">
        <h1 className="text-[28px] font-medium leading-tight tracking-[-0.015em] [text-wrap:balance]">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[13.5px] text-cover-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
    {figures.length > 0 && (
      <dl className="relative mt-7 flex flex-wrap gap-x-11 gap-y-4">
        {figures.map((f) => (
          <div key={f.label} className="flex flex-col-reverse">
            <dt className="mt-1.5 text-[12.5px] text-cover-muted">{f.label}</dt>
            <dd className="text-[32px] font-normal leading-none tracking-[-0.025em] tabular-nums">{f.value}</dd>
          </div>
        ))}
      </dl>
    )}
    {children}
  </section>
);

export const PageBody = ({ children, className }) => (
  <div className={cn('relative -mt-12 space-y-5 px-5 pb-10 sm:px-10', className)}>{children}</div>
);

// Botão sobre a capa: translúcido; `primary` vira branco com texto cobalto.
export const CoverButton = forwardRef(({ primary = false, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3.5 text-sm font-medium transition-[background-color,transform] duration-150 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-cover [&_svg]:size-4',
      primary
        ? 'bg-white text-blue-600 hover:bg-white/90 dark:text-blue-700'
        : 'border border-white/30 bg-white/15 text-cover-foreground hover:bg-white/25',
      className
    )}
    {...props}
  />
));
CoverButton.displayName = 'CoverButton';

// Cabeçalho de folha: título curto à esquerda, controles à direita, fio embaixo.
export const SheetHeader = ({ title, children, className }) => (
  <div className={cn('flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4', className)}>
    {typeof title === 'string' ? <h2 className="text-sm font-semibold">{title}</h2> : title}
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

// Status = ponto + frase. Tons dizem quem age: run (andando sozinho), att (precisa
// de alguém), ok (concluído), bad (travou), off (encerrado).
export const DOT = {
  run: 'bg-blue-500',
  att: 'bg-amber-500',
  ok: 'bg-emerald-500',
  bad: 'bg-red-500',
  off: 'bg-gray-400',
};

export const StatusDot = ({ tone = 'off', children, className }) => (
  <span
    className={cn(
      'inline-flex items-center gap-2 text-[13.5px] text-gray-600 dark:text-gray-300',
      tone === 'bad' && 'text-red-700 dark:text-red-300',
      className
    )}
  >
    <span aria-hidden className={cn('h-[7px] w-[7px] shrink-0 rounded-full', DOT[tone] || DOT.off)} />
    {children}
  </span>
);

// Busca sobre a capa (translúcida, como os botões da capa).
export const CoverSearch = ({ value, onChange, placeholder, className, ...props }) => (
  <label className={cn('flex h-9 min-w-[240px] items-center gap-2 rounded-lg border border-white/30 bg-white/15 px-3 text-sm text-cover-foreground transition-colors focus-within:bg-white/25 focus-within:ring-2 focus-within:ring-white/60', className)}>
    <Search aria-hidden className="h-4 w-4 shrink-0 text-cover-muted" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className="w-full min-w-0 bg-transparent outline-none placeholder:text-cover-muted"
      {...props}
    />
    {value && (
      <button type="button" onClick={() => onChange('')} aria-label="Limpar busca" className="rounded p-0.5 text-cover-muted transition-colors hover:text-cover-foreground">
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </label>
);

// Seletor segmentado (período, ordem). `options` = [{ value, label }].
export const Segmented = ({ options, value, onChange, label, testid, onCover = false }) => (
  <div role="group" aria-label={label} className={cn('inline-flex gap-0.5 rounded-[9px] p-0.5', onCover ? 'border border-white/25 bg-white/10' : 'bg-muted')}>
    {options.map((op) => (
      <button
        key={op.value}
        type="button"
        onClick={() => onChange(op.value)}
        aria-pressed={value === op.value}
        data-testid={testid ? `${testid}-${op.value}` : undefined}
        className={cn(
          'rounded-[7px] px-2.5 py-1 text-[12.5px] font-medium transition-colors duration-150',
          onCover
            ? value === op.value ? 'bg-white text-blue-700' : 'text-cover-muted hover:text-cover-foreground'
            : value === op.value ? 'bg-card text-foreground shadow-sm' : 'text-gray-600 hover:text-foreground dark:text-gray-400'
        )}
      >
        {op.label}
      </button>
    ))}
  </div>
);

export const PERIODOS = [
  { value: 'hoje', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'tudo', label: 'Tudo' },
];

export const legendaPeriodo = (p) => ({ hoje: 'Hoje', '7d': 'Últimos 7 dias', '30d': 'Últimos 30 dias', mes: 'Este mês', tudo: 'Todo o período' }[p] || '');
